import { useT } from '../../i18n/i18n';
import type { IconName } from '../icons';
import { IconButton } from './Button';
import { Icon } from './Icon';

// Photo card (Authority §8.4; 02 / 03 / 04 / 05): 4:3 cover thumbnail, 12px radius, 1px border,
// optional top-left tag and bottom-right corner icon, and — always — a line below the photo:
// "Note: <summary>" with an edit pencil, or "+ Add Photo Note". Notes never hide in the lightbox.
export function PhotoCard(props: {
  src: string | null;
  alt: string;
  label?: string;
  note?: string;
  noteTone?: 'default' | 'error';
  cornerIcon?: IconName;
  flagged?: boolean;
  selected?: boolean;
  onOpen?: () => void;
  onEditNote?: () => void;
  onAddNote?: () => void;
  // Owner device test 2026-10-05 (照片池的照片没有删除键): a delete button over the photo's top-right
  // corner. The caller asks for confirmation; the button only reports the tap.
  onRemove?: () => void;
  // Hooks for tests / automation on the media button (the tappable photo itself).
  mediaTestId?: string;
  albumState?: string;
  testId?: string;
}) {
  const { t } = useT();
  const hasNote = typeof props.note === 'string' && props.note.trim().length > 0;
  return (
    <figure className={`photo-card ${props.selected ? 'photo-card-selected' : ''} ${props.onRemove ? 'photo-card-removable' : ''}`} data-testid={props.testId} data-selected={props.selected ? 'true' : undefined}>
      <button
        type="button"
        className="photo-card-media"
        onClick={props.onOpen}
        aria-label={props.alt}
        aria-pressed={props.selected}
        disabled={!props.onOpen}
        data-testid={props.mediaTestId}
        data-album-state={props.albumState}
      >
        {props.src ? (
          <img src={props.src} alt="" loading="lazy" />
        ) : (
          <span className="photo-card-empty">
            <Icon name="photo_library" size={28} />
          </span>
        )}
        {props.label ? <span className="photo-card-label">{props.label}</span> : null}
        {props.flagged ? (
          <span className="photo-card-flag">
            <Icon name="error" />
          </span>
        ) : null}
        {props.cornerIcon ? (
          <span className="photo-card-corner">
            <Icon name={props.cornerIcon} />
          </span>
        ) : null}
        {props.selected ? (
          <span className="photo-card-check" data-testid="photo-card-check" aria-hidden="true">
            <Icon name="check" />
          </span>
        ) : null}
      </button>
      {props.onRemove ? (
        <button type="button" className="photo-card-remove" onClick={props.onRemove} aria-label={t('photo.deletePhoto')} data-testid="photo-remove">
          <Icon name="delete" />
        </button>
      ) : null}
      <figcaption className={`photo-card-note ${props.noteTone === 'error' ? 'photo-card-note-error' : ''}`}>
        {hasNote ? (
          <>
            {props.noteTone === 'error' ? <Icon name="warning" size={18} /> : null}
            <span className="photo-card-note-text">
              <strong>{t('photo.notePrefix')}</strong> {props.note}
            </span>
            {props.onEditNote ? <IconButton icon="edit" label={t('photo.editNote')} onClick={props.onEditNote} tone="soft" iconSize={18} /> : null}
          </>
        ) : (
          <button type="button" className="photo-card-add-note" onClick={props.onAddNote} disabled={!props.onAddNote}>
            <Icon name="add" />
            {t('photo.addPhotoNote')}
          </button>
        )}
      </figcaption>
    </figure>
  );
}

// Dashed add-photo tile (04 / 05): same footprint as a photo card; one tile carries both capture
// actions (Take Photo + Choose Library — D-05) or a single generic "Add Photo".
export function AddPhotoTile(props: { actions: Array<{ key: string; label: string; icon: IconName; onClick?: () => void; testId?: string }>; testId?: string }) {
  return (
    <div className="add-photo-tile" data-testid={props.testId}>
      <div className="add-photo-tile-actions">
        {props.actions.map((action) => (
          <button key={action.key} type="button" className="add-photo-tile-action" onClick={action.onClick} data-testid={action.testId}>
            <Icon name={action.icon} size={28} />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
